import type {
	CreatePlantAnalysisPayload,
	CreateSoilAnalysisPayload,
	PlantAnalysisDTO,
	PlantAnalysisValues,
	SoilAnalysisDTO,
	SoilAnalysisValues,
} from '@practium/features/api';
import {
	DEFAULT_PLANT_FORM,
	DEFAULT_PLANT_UNITS,
	DEFAULT_SOIL_FORM,
	DEFAULT_SOIL_UNITS,
} from '../constants/analyses.constants';
import type { PlantAnalysisFormData, SoilAnalysisFormData } from '../types/analyses.types';

type ApiRef = string | { _id?: string; name?: string } | null | undefined;

const resolveRefId = (value: ApiRef) => {
	if (!value) return '';
	if (typeof value === 'string') return value;
	return value._id ?? '';
};

const resolveSectorIds = (sectors?: ApiRef[]) => sectors?.map(resolveRefId).filter(Boolean) ?? [];

type AnalysisValue = { value: number; unit: string };

const unwrapAnalysisValue = (value: unknown) => {
	if (typeof value === 'object' && value !== null && 'value' in value) {
		return (value as { value?: unknown }).value;
	}
	return value;
};

const toFormValue = (value: unknown) => {
	const unwrappedValue = unwrapAnalysisValue(value);
	if (unwrappedValue === null || unwrappedValue === undefined) return '';
	if (typeof unwrappedValue !== 'string' && typeof unwrappedValue !== 'number') return '';
	return String(unwrappedValue);
};

const toFormUnit = (value: unknown, fallback: string) => {
	if (typeof value === 'object' && value !== null && 'unit' in value) {
		const unit = (value as { unit?: unknown }).unit;
		if (typeof unit === 'string' && unit) return unit;
	}
	return fallback;
};

const toPayloadNumber = (value: string) => {
	if (!value) return undefined;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : undefined;
};

const toAnalysisValue = (value: string, unit: string): AnalysisValue | undefined => {
	const parsedValue = toPayloadNumber(value);
	return parsedValue === undefined ? undefined : { value: parsedValue, unit };
};

const resolveTextureMode = (
	values: SoilAnalysisDTO['values']
): SoilAnalysisFormData['textureMode'] => {
	if (values.texture) return 'texture';
	if (unwrapAnalysisValue(values.clay) !== null && unwrapAnalysisValue(values.clay) !== undefined) {
		return 'clay';
	}
	if (unwrapAnalysisValue(values.Prem) !== null && unwrapAnalysisValue(values.Prem) !== undefined) {
		return 'Prem';
	}
	return '';
};

export const mapSoilAnalysisToForm = (analysis: SoilAnalysisDTO): SoilAnalysisFormData => {
	const values = analysis.values ?? {};

	return {
		...DEFAULT_SOIL_FORM,
		name: analysis.name ?? '',
		state: analysis.state ?? '',
		sampledAt: analysis.sampledAt?.split('T')[0] ?? '',
		property: resolveRefId(analysis.prop),
		area: resolveRefId(analysis.area),
		sector: resolveSectorIds(analysis.sectors),
		note: analysis.note ?? '',
		textureMode: resolveTextureMode(values),
		Prem: toFormValue(values.Prem),
		texture: values.texture ?? '',
		clay: toFormValue(values.clay),
		Pm1: toFormValue(values.Pm1),
		Pres: toFormValue(values.Pres),
		K: toFormValue(values.K),
		Ca: toFormValue(values.Ca),
		Mg: toFormValue(values.Mg),
		S: toFormValue(values.S),
		Na: toFormValue(values.Na),
		B: toFormValue(values.B),
		Cu: toFormValue(values.Cu),
		Fe: toFormValue(values.Fe),
		Mn: toFormValue(values.Mn),
		Zn: toFormValue(values.Zn),
		MO: toFormValue(values.MO),
		pHagua: toFormValue(values.pHagua),
		pHCaCl2: toFormValue(values.pHCaCl2),
		Al: toFormValue(values.Al),
		HAl: toFormValue(values.HAl),
		SB: toFormValue(values.SB),
		ctcE: toFormValue(values.ctcE),
		ctcT: toFormValue(values.ctcT),
		CaMg: toFormValue(values.CaMg),
		satSB: toFormValue(values.satSB),
		satAl: toFormValue(values.satAl),
		satCa: toFormValue(values.satCa),
		satMg: toFormValue(values.satMg),
		satK: toFormValue(values.satK),
		units: {
			Prem: toFormUnit(values.Prem, DEFAULT_SOIL_UNITS.Prem),
			clay: toFormUnit(values.clay, DEFAULT_SOIL_UNITS.clay),
			Pm1: toFormUnit(values.Pm1, DEFAULT_SOIL_UNITS.Pm1),
			Pres: toFormUnit(values.Pres, DEFAULT_SOIL_UNITS.Pres),
			K: toFormUnit(values.K, DEFAULT_SOIL_UNITS.K),
			Ca: toFormUnit(values.Ca, DEFAULT_SOIL_UNITS.Ca),
			Mg: toFormUnit(values.Mg, DEFAULT_SOIL_UNITS.Mg),
			S: toFormUnit(values.S, DEFAULT_SOIL_UNITS.S),
			Na: toFormUnit(values.Na, DEFAULT_SOIL_UNITS.Na),
			B: toFormUnit(values.B, DEFAULT_SOIL_UNITS.B),
			Cu: toFormUnit(values.Cu, DEFAULT_SOIL_UNITS.Cu),
			Fe: toFormUnit(values.Fe, DEFAULT_SOIL_UNITS.Fe),
			Mn: toFormUnit(values.Mn, DEFAULT_SOIL_UNITS.Mn),
			Zn: toFormUnit(values.Zn, DEFAULT_SOIL_UNITS.Zn),
			MO: toFormUnit(values.MO, DEFAULT_SOIL_UNITS.MO),
			pHagua: toFormUnit(values.pHagua, DEFAULT_SOIL_UNITS.pHagua),
			pHCaCl2: toFormUnit(values.pHCaCl2, DEFAULT_SOIL_UNITS.pHCaCl2),
			Al: toFormUnit(values.Al, DEFAULT_SOIL_UNITS.Al),
			HAl: toFormUnit(values.HAl, DEFAULT_SOIL_UNITS.HAl),
			SB: toFormUnit(values.SB, DEFAULT_SOIL_UNITS.SB),
			ctcE: toFormUnit(values.ctcE, DEFAULT_SOIL_UNITS.ctcE),
			ctcT: toFormUnit(values.ctcT, DEFAULT_SOIL_UNITS.ctcT),
			CaMg: toFormUnit(values.CaMg, DEFAULT_SOIL_UNITS.CaMg),
			satSB: toFormUnit(values.satSB, DEFAULT_SOIL_UNITS.satSB),
			satAl: toFormUnit(values.satAl, DEFAULT_SOIL_UNITS.satAl),
			satCa: toFormUnit(values.satCa, DEFAULT_SOIL_UNITS.satCa),
			satMg: toFormUnit(values.satMg, DEFAULT_SOIL_UNITS.satMg),
			satK: toFormUnit(values.satK, DEFAULT_SOIL_UNITS.satK),
		},
	};
};

export const mapSoilFormToCreatePayload = (
	form: SoilAnalysisFormData,
	userId: string
): CreateSoilAnalysisPayload => {
	const values: SoilAnalysisValues = {
		Pm1: toAnalysisValue(form.Pm1, form.units.Pm1),
		Pres: toAnalysisValue(form.Pres, form.units.Pres),
		K: toAnalysisValue(form.K, form.units.K),
		Ca: toAnalysisValue(form.Ca, form.units.Ca),
		Mg: toAnalysisValue(form.Mg, form.units.Mg),
		S: toAnalysisValue(form.S, form.units.S),
		Na: toAnalysisValue(form.Na, form.units.Na),
		B: toAnalysisValue(form.B, form.units.B),
		Cu: toAnalysisValue(form.Cu, form.units.Cu),
		Fe: toAnalysisValue(form.Fe, form.units.Fe),
		Mn: toAnalysisValue(form.Mn, form.units.Mn),
		Zn: toAnalysisValue(form.Zn, form.units.Zn),
		MO: toAnalysisValue(form.MO, form.units.MO),
		pHagua: toAnalysisValue(form.pHagua, form.units.pHagua),
		pHCaCl2: toAnalysisValue(form.pHCaCl2, form.units.pHCaCl2),
		Al: toAnalysisValue(form.Al, form.units.Al),
		HAl: toAnalysisValue(form.HAl, form.units.HAl),
		SB: toAnalysisValue(form.SB, form.units.SB),
		ctcE: toAnalysisValue(form.ctcE, form.units.ctcE),
		ctcT: toAnalysisValue(form.ctcT, form.units.ctcT),
		CaMg: toAnalysisValue(form.CaMg, form.units.CaMg),
		satSB: toAnalysisValue(form.satSB, form.units.satSB),
		satAl: toAnalysisValue(form.satAl, form.units.satAl),
		satCa: toAnalysisValue(form.satCa, form.units.satCa),
		satMg: toAnalysisValue(form.satMg, form.units.satMg),
		satK: toAnalysisValue(form.satK, form.units.satK),
	};

	if (form.textureMode === 'Prem') values.Prem = toAnalysisValue(form.Prem, form.units.Prem);
	if (form.textureMode === 'texture') values.texture = form.texture || undefined;
	if (form.textureMode === 'clay') values.clay = toAnalysisValue(form.clay, form.units.clay);

	return {
		user: userId,
		prop: form.property,
		area: form.area,
		sectors: form.sector.length > 0 ? form.sector : undefined,
		state: form.state || undefined,
		name: form.name,
		values,
		note: form.note || undefined,
		sampledAt: form.sampledAt,
	};
};

export const mapPlantAnalysisToForm = (analysis: PlantAnalysisDTO): PlantAnalysisFormData => {
	const values = analysis.values ?? {};

	return {
		...DEFAULT_PLANT_FORM,
		name: analysis.name ?? '',
		region: analysis.region ?? '',
		sampleAt: analysis.sampleAt?.split('T')[0] ?? '',
		property: resolveRefId(analysis.prop),
		area: resolveRefId(analysis.area),
		sector: resolveSectorIds(analysis.sectors),
		culture: analysis.culture ?? '',
		season: analysis.season ?? '',
		biennial: analysis.biennial ?? '',
		note: analysis.note ?? '',
		N: toFormValue(values.N),
		P: toFormValue(values.P),
		K: toFormValue(values.K),
		Ca: toFormValue(values.Ca),
		Mg: toFormValue(values.Mg),
		S: toFormValue(values.S),
		B: toFormValue(values.B),
		Cu: toFormValue(values.Cu),
		Fe: toFormValue(values.Fe),
		Mn: toFormValue(values.Mn),
		Zn: toFormValue(values.Zn),
		units: {
			N: toFormUnit(values.N, DEFAULT_PLANT_UNITS.N),
			P: toFormUnit(values.P, DEFAULT_PLANT_UNITS.P),
			K: toFormUnit(values.K, DEFAULT_PLANT_UNITS.K),
			Ca: toFormUnit(values.Ca, DEFAULT_PLANT_UNITS.Ca),
			Mg: toFormUnit(values.Mg, DEFAULT_PLANT_UNITS.Mg),
			S: toFormUnit(values.S, DEFAULT_PLANT_UNITS.S),
			B: toFormUnit(values.B, DEFAULT_PLANT_UNITS.B),
			Cu: toFormUnit(values.Cu, DEFAULT_PLANT_UNITS.Cu),
			Fe: toFormUnit(values.Fe, DEFAULT_PLANT_UNITS.Fe),
			Mn: toFormUnit(values.Mn, DEFAULT_PLANT_UNITS.Mn),
			Zn: toFormUnit(values.Zn, DEFAULT_PLANT_UNITS.Zn),
		},
	};
};

export const mapPlantFormToCreatePayload = (
	form: PlantAnalysisFormData,
	userId: string
): CreatePlantAnalysisPayload => ({
	name: form.name,
	user: userId,
	prop: form.property,
	area: form.area,
	sectors: form.sector,
	culture: form.culture,
	sampleAt: form.sampleAt,
	season: form.season || undefined,
	region: form.region || undefined,
	biennial: form.biennial || undefined,
	note: form.note || undefined,
	values: {
		N: toAnalysisValue(form.N, form.units.N),
		P: toAnalysisValue(form.P, form.units.P),
		K: toAnalysisValue(form.K, form.units.K),
		Ca: toAnalysisValue(form.Ca, form.units.Ca),
		Mg: toAnalysisValue(form.Mg, form.units.Mg),
		S: toAnalysisValue(form.S, form.units.S),
		B: toAnalysisValue(form.B, form.units.B),
		Cu: toAnalysisValue(form.Cu, form.units.Cu),
		Fe: toAnalysisValue(form.Fe, form.units.Fe),
		Mn: toAnalysisValue(form.Mn, form.units.Mn),
		Zn: toAnalysisValue(form.Zn, form.units.Zn),
	} satisfies PlantAnalysisValues,
});
